import React, { useState } from 'react';
import { X, AlertTriangle, Trash2 } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';


export default function BulkActionsModal({ isOpen, onClose, selectedOrgs, action, onConfirm }) {
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      handleClose();
    } catch (error) {
      console.error('Bulk action error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setConfirmText('');
    onClose();
  };

  if (!isOpen) return null;

  const actionConfig = {
    suspend: {
      title: 'Suspend Organizations',
      description: 'Suspended organizations will be deactivated and users will lose access.',
      color: 'orange',
      icon: AlertTriangle,
      confirmRequired: false
    },
    activate: {
      title: 'Activate Organizations',
      description: 'Organizations will be reactivated and users will regain access.',
      color: 'green',
      icon: AlertTriangle,
      confirmRequired: false
    },
    delete: {
      title: 'Delete Organizations',
      description: 'This action cannot be undone. All organization data will be permanently deleted.',
      color: 'red',
      icon: Trash2,
      confirmRequired: true,
      confirmPhrase: 'DELETE'
    }
  };

  const config = actionConfig?.[action] || actionConfig?.suspend;
  const Icon = config?.icon;
  const canConfirm = !config?.confirmRequired || confirmText === config?.confirmPhrase;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg bg-${config?.color}-100 flex items-center justify-center`}>
              <Icon className={`w-5 h-5 text-${config?.color}-600`} />
            </div>
            <h2 className="text-xl font-bold text-gray-900">{config?.title}</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className={`bg-${config?.color}-50 border border-${config?.color}-200 rounded-lg p-4`}>
            <p className={`text-${config?.color}-800 text-sm`}>{config?.description}</p>
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">
              Selected Organizations ({selectedOrgs?.length}):
            </p>
            <div className="max-h-40 overflow-y-auto bg-gray-50 rounded-lg p-3 space-y-1">
              {selectedOrgs?.map((org) => (
                <div key={org?.id} className="text-sm text-gray-700">
                  • {org?.name}
                </div>
              ))}
            </div>
          </div>

          {config?.confirmRequired && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type <span className="font-bold text-red-600">{config?.confirmPhrase}</span> to confirm:
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder={config?.confirmPhrase}
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <Button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading || !canConfirm}
            className={`px-4 py-2 bg-${config?.color}-600 text-white rounded-lg hover:bg-${config?.color}-700 disabled:opacity-50`}
          >
            {loading ? 'Processing...' : `Confirm ${config?.title}`}
          </Button>
        </div>
      </div>
    </div>
  );
}